// @Deprecated: This file is deprecated and will be removed in the future.
// TODO LEGACY DEPRECATION: Remove this file when deprecating legacy app
// ** As part of Redesign, Contact Details Edit is calling same modal as Contacts List page (https://capintel.atlassian.net/browse/CONN-5771)

// Third-party
import React from 'react';
import { FormikProps, withFormik } from 'formik';

// Components
import { CancelButton, PrimaryButton } from '../../../components/buttons';
import { ContactPastille } from '../../../components/misc';
import { FormInput } from '../../../components/forms';

// Utils
import { FormattedMessage, LOCALE_OPTIONS, formatMessage } from '../../../../utils/i18n';
import notification from '../../../../utils/notification';
import { getContactLayout, CONTACT_LAYOUT } from '../../../../utils/contacts';
import User from '@/types/user';
import GenericContact from '@/types/generic-contact';
import Locale from '@/types/locale';
import { SelectInput, SelectOption } from '@/views/components/inputs';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

type EditContactFields = {
  uid: string;
  fname: string;
  lname?: string;
  email?: string;
  locale: Locale;
  note: string;
};

type ParentProps = {
  user: User;
  contact: GenericContact;
  isGIF?: boolean;
  onCancel: () => void;
  onSave: (contact: GenericContact) => Promise<GenericContact>;
};

type Props = FormikProps<EditContactFields> & ParentProps;
class ContactDetailsEdit extends React.Component<Props> {
  canSubmit = () => {
    const { values, isSubmitting, errors, user } = this.props;

    const hasErrors = Object.keys(errors).length > 0;
    const { fname, lname } = values;

    const hasContactLastName = userHasPermission(user, PERMISSIONS.CONTACTS_LAST_NAME);

    return !hasErrors && !isSubmitting && fname.trim().length > 0 && (!hasContactLastName || (lname && lname.trim().length > 0));
  };

  onNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.props.setFieldValue('note', event.target.value);
  };

  render() {
    const { contact, values, user } = this.props;

    const filteredLocales = LOCALE_OPTIONS.filter(localeOption => user.appConfig.appLocales.includes(localeOption.value));
    const onLocaleChange = (value: any) => {
      this.props.setFieldValue('locale', value);
    };

    const hasContactLocale = userHasPermission(this.props.user, PERMISSIONS.CONTACTS_LOCALE);
    const canEditLocale = hasContactLocale && user.appConfig.appLocales.length > 1;
    const hasContactLastName = userHasPermission(user, PERMISSIONS.CONTACTS_LAST_NAME);
    const hasContactEmail = userHasPermission(user, PERMISSIONS.CONTACTS_EMAIL);
    const hasContactNotes = userHasPermission(user, PERMISSIONS.CONTACTS_NOTES);

    return (
      <div className="contact-details-edit">
        <div className="contact-edit-actions">
          <CancelButton compact onClick={this.props.onCancel} />

          <PrimaryButton compact onClick={this.props.submitForm} disabled={!this.canSubmit()}>
            <FormattedMessage id="Common.SaveChanges" />
          </PrimaryButton>
        </div>

        <ContactPastille contact={contact} />

        <div className="contact-details-wrapper">
          <FormattedMessage id="Contacts.ContactDetailsInfo.BasicInformation" />

          <div className="contact-fields-group full-width">
            <div className="contact-field">
              <span className="field-label">
                <FormattedMessage id="Contacts.ContactDetailsInfo.ID" />
              </span>

              <FormInput compact name="uid" placeholder={formatMessage('Contacts.ContactDetailsInfo.Placeholders.ID')} data-private data-dd-privacy="mask" />
            </div>
          </div>

          <div className={`contact-fields-group ${hasContactLastName ? '' : 'full-width'}`}>
            <div className="contact-field">
              <span className="field-label">
                <FormattedMessage id="Contacts.ContactDetailsInfo.FirstName" />
                <span>*</span>
              </span>

              <FormInput
                required
                compact
                name="fname"
                placeholder={formatMessage('Contacts.ContactDetailsInfo.Placeholders.FirstName')}
                data-private
                data-dd-privacy="mask"
              />
            </div>

            {hasContactLastName && (
              <div className="contact-field">
                <span className="field-label">
                  <FormattedMessage id="Contacts.ContactDetailsInfo.LastName" />
                  <span>*</span>
                </span>

                <FormInput
                  compact
                  required
                  name="lname"
                  placeholder={formatMessage('Contacts.ContactDetailsInfo.Placeholders.LastName')}
                  data-private
                  data-dd-privacy="mask"
                />
              </div>
            )}
          </div>

          {hasContactEmail && (
            <div className="contact-fields-group full-width">
              <div className="contact-field">
                <span className="field-label">
                  <FormattedMessage id="Contacts.ContactDetailsInfo.Email" />
                </span>

                <FormInput
                  compact
                  required
                  name="email"
                  placeholder={formatMessage('Contacts.ContactDetailsInfo.Placeholders.Email')}
                  data-private
                  data-dd-privacy="mask"
                />
              </div>
            </div>
          )}

          {canEditLocale && (
            <div className="contact-fields-group full-width">
              <div className="contact-field">
                <span className="field-label">
                  <FormattedMessage id="Contacts.ContactDetailsInfo.Locale" />*
                </span>

                <SelectInput onValueChange={onLocaleChange} selected={values.locale}>
                  {filteredLocales.map(localeOption => (
                    <SelectOption key={localeOption.value} value={localeOption.value}>
                      {localeOption.label}
                    </SelectOption>
                  ))}
                </SelectInput>
              </div>
            </div>
          )}

          {hasContactNotes && (
            <div className="contact-fields-group full-width">
              <div className="contact-field">
                <span className="field-label">
                  <FormattedMessage id="Contacts.ContactDetailsInfo.Notes" data-private data-dd-privacy="mask" />
                </span>

                <textarea
                  data-private
                  data-dd-privacy="mask"
                  maxLength={1000}
                  onChange={this.onNoteChange}
                  defaultValue={contact.note}
                  placeholder={formatMessage('Contacts.ContactDetailsInfo.Placeholders.Note')}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default withFormik<ParentProps, EditContactFields>({
  mapPropsToValues: ({ contact, user }) => {
    const defaultLocale = user.authUser.locale || user.appConfig.appLocales[0];
    const result: EditContactFields = {
      fname: contact.fname || '',
      locale: contact.locale || defaultLocale,
      uid: contact.uid || '',
      note: contact.note || '',
    };

    if (userHasPermission(user, PERMISSIONS.CONTACTS_LAST_NAME)) {
      result.lname = contact.lname || '';
    }

    if (userHasPermission(user, PERMISSIONS.CONTACTS_EMAIL)) {
      result.email = contact.email || '';
    }

    return result;
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    const contact: Partial<GenericContact> = {
      ...props.contact,
      ...values,
      _id: props.contact._id,
    };

    setSubmitting(true);

    props.onSave(contact).then(() => {
      const contactLayout = getContactLayout(props.user);

      setSubmitting(false);

      if (contactLayout === CONTACT_LAYOUT.CLIENT) {
        notification.success(formatMessage(`EditContact.${contact._id ? 'EditSuccessClient' : 'CreateSuccessClient'}`));
      } else {
        notification.success(formatMessage(`EditContact.${contact._id ? 'EditSuccess' : 'CreateSuccess'}`));
      }
    });
  },
})(ContactDetailsEdit);
